import { realtime } from '../api/realtime.js';
import { audioManager } from '../audio/AudioManager.js';
import { RemoteAvatar } from './RemoteAvatar.js';
import { NpcAvatar } from './NpcAvatar.js';
import { reconcileRoster } from './rosterReconcile.mjs';
import { perchAnchor as perchAnchorIn } from './furnitureSeatsLogic.mjs';
import { preloader } from '../scenes/PreloadScene.js';

// Attach a scene to one room: spawns/updates/removes RemoteAvatars from server events,
// drives their interpolation each frame, and detaches on scene shutdown.
// opts.isHidden(peerId) → keep that peer's avatar invisible (e.g. a passenger riding in someone's car).
export function attachRemotePresence(scene, room, opts = {}) {
  const avatars = new Map();  // peerId -> RemoteAvatar
  const offs = [];
  // A peer resting on a sofa / bed is drawn on its spot in this room (the scene's FurnitureSeats), else standing.
  const perchAnchor = opts.perchAnchor
    ?? ((perch) => (scene.furnitureSeats ? perchAnchorIn(perch, scene.furnitureSeats.boxes()) : null));

  // Seated peers are drawn at the seat anchor, not at the (possibly stale) coordinates they
  // last broadcast while walking; opts.seatAnchor(seat) → {x, y} centre, or null.
  const snap = (m) => {
    if (m?.mode !== 'sit' || !opts.seatAnchor) return m;
    const a = opts.seatAnchor(m.seat);
    return a ? { ...m, x: a.x, y: a.y } : m;
  };

  // Server-driven NPCs arrive through the same roster/join/move messages as players, tagged
  // `mode: 'npc'` with an `npc:` id — they render as a single static sprite (NpcAvatar), never
  // through the outfit pipeline. `opts.onNpc` lets a scene grab one (e.g. the instructor).
  // How many players (not NPCs) share the room: a phone keeps fewer neighbours' backgrounds in a crowd (Gate M)
  const crowd = () => preloader.setCrowd([...avatars.values()].filter((a) => !(a instanceof NpcAvatar)).length);
  const add = (peer) => {
    if (!peer?.id || avatars.has(peer.id)) return;
    const isNpc = peer.mode === 'npc' || String(peer.id).startsWith('npc:');
    const avatar = isNpc ? new NpcAvatar(scene, snap(peer)) : new RemoteAvatar(scene, snap(peer), { perchAnchor });
    if (!isNpc && opts.isHidden?.(peer.id)) avatar.setHidden(true);
    avatars.set(peer.id, avatar);
    if (isNpc) opts.onNpc?.(peer.id, avatar);
    crowd();
  };
  const remove = (peerId) => {
    avatars.get(peerId)?.destroy();
    avatars.delete(peerId);
    crowd();
  };

  offs.push(realtime.on('roster', (m) => {
    if (m.room !== room) return;
    // A fresh roster (first join, or a reconnect after being inactive / a dropped socket) is the
    // whole room: drop whoever left meanwhile, catch up those still here, then add newcomers.
    const { stale, kept } = reconcileRoster(avatars.keys(), m.peers);
    stale.forEach(remove);
    for (const p of kept) {
      const a = avatars.get(p.id);
      if (p.mode === 'npc' || String(p.id).startsWith('npc:')) continue;   // NPCs move by their own ticker
      a.pushMove(snap(p));
      a.setSleeping(p.idle);
      if (p.outfit) a.applyOutfit(p.outfit);
    }
    m.peers.forEach(add);
  }));
  offs.push(realtime.on('join',  (m) => { if (m.room === room) add(m.peer); }));
  offs.push(realtime.on('leave', (m) => { if (m.room === room) remove(m.peerId); }));
  offs.push(realtime.on('move',  (m) => {
    if (m.room !== room) return;
    // The NPC ticker may broadcast before our roster arrives on reconnect — spawn it here.
    if (!avatars.has(m.peerId) && m.mode === 'npc') add({ ...m, id: m.peerId });
    avatars.get(m.peerId)?.pushMove(snap(m));
  }));
  offs.push(realtime.on('outfit', (m) => { if (m.room === room) avatars.get(m.peerId)?.applyOutfit(m.outfit); }));
  offs.push(realtime.on('idle', (m) => { if (m.room === room) avatars.get(m.peerId)?.setSleeping(m.idle); }));
  offs.push(realtime.on('chat', (m) => {
    if (m.room !== room) return;
    if (m.peerId !== window.__myId) audioManager.playSfx('message_in');  // don't blip on our own echoed message
    const text = m.text.startsWith('palette:') ? m.text.slice('palette:'.length) : m.text;
    avatars.get(m.peerId)?.setChat(text);
  }));

  realtime.subscribe(room);

  const onUpdate = () => { for (const a of avatars.values()) a.update(); };
  scene.events.on('update', onUpdate);

  const detach = () => {
    realtime.unsubscribe(room);
    scene.events.off('update', onUpdate);
    offs.forEach(off => off());
    for (const a of avatars.values()) a.destroy();
    avatars.clear();
    crowd();
  };
  scene.events.once('shutdown', detach);
  return { detach, avatars };
}
